import { z } from "zod";
//#region src/dp-live-status.ts
/**
* The two status reads of one data plane that `zion-ops dp status` shows:
*
* - `GET /v1/admin/data-planes/:id/live-status`: the latest status report of the DP operator.
*   Producer: site-admin-api `DpAdminService.getLiveStatus`, the type `DpLiveStatusSummary`, read
*   from `release.dp_live_status` by `liveRowToSummary`. A DP that never reported answers 404.
* - `GET /v1/admin/data-planes/:id/health`: the lifecycle health. Producer: provisioning-svc
*   `ProvisioningService.getHealth`, proxied unchanged by `DpAdminService.getAggregatedHealth`.
*
* Plan `2026-10-21-operator-tui`, defaults row F1.
*
* The nested report members (`services`, `lastMigration`, `observability`, `lastEnvelopeErrors`)
* come from the DP operator's report, which release-engine checks against `StatusReportSchema` of
* `@zion/sdk-cp-dp-contracts` before it stores the row. That package depends on this one, so this
* file cannot import those schemas. It restates the members a reader uses, and it keeps the two
* enums (`health`, `kind`) as text, because a row that an older DP build wrote stays in the table.
*/
/** One platform service of the DP, as the operator reported it. */
const dpLiveServiceStatusSchema = z.object({
	version: z.string(),
	health: z.string(),
	readyPods: z.number().int().nonnegative(),
	desiredPods: z.number().int().nonnegative()
}).strict();
/** The last migration the operator reported. */
const dpLiveMigrationStatusSchema = z.object({
	service: z.string().min(1),
	migrationId: z.string().min(1),
	completedAt: z.string().min(1),
	durationMs: z.number().int().nonnegative()
}).strict();
/** The observability settings the operator applied. */
const dpLiveObservabilityStatusSchema = z.object({
	retentionDays: z.number().int().nonnegative(),
	samplingRate: z.number().min(0).max(1),
	pushIntervalSeconds: z.number().int().positive(),
	telemetryMode: z.string(),
	lastConfigChangeAt: z.string().min(1),
	errors: z.array(z.string())
}).strict();
/**
* One envelope error. `kind` is a wire kind of the operator or the CP's own `fixture-unhealthy`.
* `appId` is absent on the three envelope-level kinds.
*/
const dpLiveEnvelopeErrorSchema = z.object({
	appId: z.string().min(1).optional(),
	kind: z.string().min(1),
	fixtureId: z.string().min(1).optional(),
	fixtureVersion: z.string().min(1).optional(),
	errors: z.array(z.object({
		instancePath: z.string(),
		message: z.string()
	}).strict())
}).strict();
/**
* `GET /v1/admin/data-planes/:id/live-status`.
*
* - `reportedAt` is the DP's own clock; `receivedAt` is the CP's. Freshness reads `receivedAt`.
* - `apps` is the per-app report, keyed by app id. Its values are opaque to this read: the console
*   and the CLI show the keys and a count. The app routes carry the typed per-app state.
* - `lastMigration` and `observability` are `null` when the last report carried none.
* - `lastEnvelopeErrors` is `null` when the last report carried no envelope error, never `[]`.
*/
const dpLiveStatusResponseSchema = z.object({
	dataPlaneId: z.uuid(),
	platformVersion: z.string(),
	helmChartVersion: z.string(),
	operatorVersion: z.string(),
	health: z.string(),
	services: z.record(z.string(), dpLiveServiceStatusSchema),
	apps: z.record(z.string(), z.unknown()),
	lastMigration: dpLiveMigrationStatusSchema.nullable(),
	observability: dpLiveObservabilityStatusSchema.nullable(),
	reportedAt: z.iso.datetime(),
	receivedAt: z.iso.datetime(),
	needsRefresh: z.boolean(),
	operatorStale: z.boolean(),
	operatorUnresponsive: z.boolean(),
	lastEnvelopeErrors: z.array(dpLiveEnvelopeErrorSchema).nullable()
}).strict();
/**
* `GET /v1/admin/data-planes/:id/health`.
*
* - `status`: the lifecycle state of the placement row.
* - `workflowState`, `jobStatus`, `jobId`, `lastError`, `lastTransitionAt`: from the newest
*   provisioning job, each `null` when the DP has no job. `lastError` has secrets redacted.
* - `healthy`: a lifecycle derivation (the DP is in the operating fleet), not a probe.
*/
const dpHealthResponseSchema = z.object({
	dataPlaneId: z.uuid(),
	status: z.string().min(1),
	workflowState: z.string().nullable(),
	jobStatus: z.string().nullable(),
	jobId: z.uuid().nullable(),
	lastError: z.string().nullable(),
	lastTransitionAt: z.iso.datetime().nullable(),
	healthy: z.boolean()
}).strict();
//#endregion
export { dpHealthResponseSchema, dpLiveEnvelopeErrorSchema, dpLiveMigrationStatusSchema, dpLiveObservabilityStatusSchema, dpLiveServiceStatusSchema, dpLiveStatusResponseSchema };

//# sourceMappingURL=dp-live-status.js.map